import { useState, useRef } from 'react';
import { ScrollText, History, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { LogView } from './LogView';
import { QueryHistoryView } from './QueryHistoryView';

type SubTab = 'log' | 'history';

interface BottomPanelProps {
  collapsed: boolean;
  onToggle: () => void;
  height: number;
}

export function BottomPanel({ collapsed, onToggle, height }: BottomPanelProps) {
  const [activeTab, setActiveTab] = useState<SubTab>('log');
  const clearLogRef = useRef<(() => void) | null>(null);

  const handleClear = () => {
    if (activeTab === 'log') {
      clearLogRef.current?.();
    }
  };

  const handleTabClick = (tab: SubTab) => {
    if (collapsed) {
      // Clicking a tab when collapsed should expand
      onToggle();
    }
    setActiveTab(tab);
  };

  return (
    <div
      className="flex flex-col bg-dbgo-bottom-panel border-t border-subtle flex-shrink-0 overflow-hidden"
      style={{ height }}
    >
      {/* Header bar - always visible */}
      <div className="h-[30px] flex items-center px-2 flex-shrink-0 gap-0.5">
        {/* Sub-tab buttons */}
        <button
          onClick={() => handleTabClick('log')}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] font-medium transition-colors ${
            activeTab === 'log'
              ? 'text-accent-blue'
              : 'text-text-muted hover:text-text-secondary'
          }`}
        >
          <ScrollText size={13} />
          Log
        </button>
        <button
          onClick={() => handleTabClick('history')}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] font-medium transition-colors ${
            activeTab === 'history'
              ? 'text-accent-blue'
              : 'text-text-muted hover:text-text-secondary'
          }`}
        >
          <History size={13} />
          Query History
        </button>

        <div className="flex-1" />

        {/* Clear button */}
        {!collapsed && activeTab === 'log' && (
          <button
            onClick={handleClear}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
            title="Clear log"
          >
            <Trash2 size={13} />
          </button>
        )}

        {/* Collapse/expand toggle */}
        <button
          onClick={onToggle}
          className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          title={collapsed ? 'Expand panel (Cmd+J)' : 'Collapse panel (Cmd+J)'}
        >
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Active tab indicator line */}
      {!collapsed && (
        <div className="h-[1px] bg-subtle relative flex-shrink-0">
          {/* We use a simple full-width border; the active tab text color serves as indicator */}
        </div>
      )}

      {/* Content area */}
      {!collapsed && (
        <div className="flex-1 min-h-0">
          {activeTab === 'log' && <LogView onClearRef={clearLogRef} />}
          {activeTab === 'history' && <QueryHistoryView />}
        </div>
      )}
    </div>
  );
}
